import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import pluralize from 'pluralize';

import log from 'client/lib/log';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrBox from 'client/components/SrBox';
import SrIcon from 'client/components/SrIcon';
import SrTooltip from 'client/components/SrTooltip';

import {
  getApplicationLimit,
} from 'common/householdApplication/applicationHelpers';

const debug = log.debug('sunroom:components:SrRentersApplyingToListingAlert');

const SrRentersApplyingToListingAlert = ({ householdsApplyingToListing, listing, rounded }) => {
  debug({ householdsApplyingToListing });
  if (!householdsApplyingToListing) {
    return null;
  }
  const householdsTentativeQualified = householdsApplyingToListing.tentativeQualifiedAndProcessing.excludingCurrentHousehold; // eslint-disable-line max-len
  const householdsAccepted = householdsApplyingToListing.accepted.excludingCurrentHousehold;

  if (householdsTentativeQualified >= getApplicationLimit(listing)) {
    return (
      <SrFlex
        align='start'
        justify='start'
        bgColor='red200'
        className={classnames('SrRentersApplyingToListingAlert', rounded && 'rounded')}
      >
        <SrIcon className='person-filled-red' marginTop={5} marginRight={8} />
        <span>
          <SrText weight='bold' color='red'>
            {`There are ${householdsTentativeQualified} households who have applied to this property. `}
          </SrText>
          <SrText weight='medium' color='red'>
            If one of the applications pulls out, you can apply then.
          </SrText>
        </span>
      </SrFlex>
    );
  }

  if (householdsAccepted > 0) {
    return (
      <SrFlex
        align='center'
        justify='between'
        bgColor='orange200'
        className={classnames('SrRentersApplyingToListingAlert', rounded && 'rounded')}
      >
        <SrFlex align='center'>
          <SrIcon className='person-filled-orange' marginRight={8} />
          <SrText weight='bold' color='orange300'>
            An applicant has been approved.
          </SrText>
        </SrFlex>
        <SrBox>
          <SrTooltip
            icon={<SrIcon className='question-mark-circle-outline' />}
            bgColor='orange600'
            className='other-renters-applying'
          >
            {
              'Someone else has already been approved for this rental. They haven’t ' +
              'paid the security deposit yet. If you’d like to apply, you’ll be a backup ' +
              'application, but we will not refund your application fees.'
            }
          </SrTooltip>
        </SrBox>
      </SrFlex>
    );
  }

  if (householdsTentativeQualified > 0) {
    return (
      <SrFlex
        align='center'
        justify='between'
        bgColor='orange200'
        className={classnames('SrRentersApplyingToListingAlert', rounded && 'rounded')}
      >
        <SrFlex align='center'>
          <SrIcon className='person-filled-orange' marginRight={8} />
          <SrText weight='medium' color='orange300'>
            <SrText weight='bold' color='orange300'>
              {pluralize(
                'other household', householdsTentativeQualified, true)}
            </SrText>
            {` ${householdsTentativeQualified === 1 ? 'has' : 'have'} applied`}
          </SrText>
        </SrFlex>
        <SrBox>
          <SrTooltip
            icon={<SrIcon className='question-mark-circle-outline' />}
            bgColor='orange600'
            className='other-renters-applying'
          >
            Other applications have not been approved yet. Feel free to apply as they may get rejected.
          </SrTooltip>
        </SrBox>
      </SrFlex>
    );
  }

  return (
    <SrFlex
      align='center'
      justify='between'
      bgColor='green-lighter'
      className={classnames('SrRentersApplyingToListingAlert', rounded && 'rounded')}
    >
      <SrFlex align='center'>
        <SrIcon className='person-filled-green' marginRight={8} />
        <SrText weight='bold' color='green'>
          No qualified applications right now!
        </SrText>
      </SrFlex>
      <SrBox>
        <SrTooltip
          icon={<SrIcon className='question-mark-circle-outline-green' />}
          bgColor='green-dark'
          className='other-renters-applying'
        >
          Be the first in line! There are currently no other renters who have applied.
        </SrTooltip>
      </SrBox>
    </SrFlex>
  );
};

SrRentersApplyingToListingAlert.propTypes = {
  householdsApplyingToListing: PropTypes.object,
  rounded: PropTypes.bool,
};

SrRentersApplyingToListingAlert.displayName = 'SrRentersApplyingToListingAlert';

export default SrRentersApplyingToListingAlert;
